import { trackUiEvent } from '@/services/uiAnalytics'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, NavLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { motion } from 'framer-motion'

import { Button } from '@/components/ui/button.jsx'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.jsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.jsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { TypographyLarge, TypographySmallMuted } from '@/components/ui/typography.jsx'

import { CollapsibleNavSection } from '@/layouts/dashboard/nav/CollapsibleNavSection.jsx'
import { SidebarAreaSwitcher } from '@/layouts/dashboard/nav/SidebarAreaSwitcher.jsx'

import { SpaceSelector } from '@/components/space-selector/SpaceSelector.jsx'
import { CreativeAnalyticsSubNavbar } from '@/layouts/dashboard/nav/subnavs/CreativeAnalyticsSubNavbar.jsx'
import { WatchedBrandsList } from '@/layouts/dashboard/nav/subnavs/WatchedBrandsList.jsx'
import { SettingsSubNavbar } from '@/layouts/dashboard/nav/subnavs/SettingsSubNavbar.jsx'
import { ChatSubNavbar } from '@/layouts/dashboard/nav/subnavs/ChatSubNavbar.jsx'
import { ClipsLibrarySubNavbar } from '@/layouts/dashboard/nav/subnavs/ClipsLibrarySubNavbar.jsx'
import { AddBrandDialog } from '@/pages/dashboard/intelligence/_components/AddBrandDialog.jsx'
import { SidebarBanner } from '@/layouts/dashboard/nav/SidebarBanner.jsx'

import { RoleGuard } from '@/guards/RoleGuard.jsx'
import { UserRoles } from '@/consts/index.js'
import { toggleInvitationDialog } from '@/hooks/useInviteMember.jsx'
import { usePersistedState } from '@/hooks/usePersistedState.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useWatchList } from '@/context-state/WatchListContext.jsx'
import { AnalyticsService } from '@/services/analytics.js'

const SIDEBAR_WIDTH = 'w-64'

// API developer-console sub-nav. `match` decides the active row: Overview is
// active on the /api index, the rest on their own child path.
const API_NAV = [
    { to: 'api', icon: 'lucide:layout-dashboard', label: 'Overview', match: (p) => /\/dashboard\/api\/?$/.test(p) },
    { to: 'api/usage', icon: 'lucide:activity', label: 'Usage', match: (p) => p.includes('/dashboard/api/usage') },
    { to: 'api/billing', icon: 'lucide:credit-card', label: 'Billing', match: (p) => p.includes('/dashboard/api/billing') },
    { to: 'api/support', icon: 'lucide:life-buoy', label: 'Support', match: (p) => p.includes('/dashboard/api/support') },
]

// Which top-level area a route belongs to. API owns /dashboard/api; Discovery
// owns everything under /intelligence (Ads Library, Boards, Watch List);
// Reporting owns the rest.
const getAreaFromPathname = (pathname) => {
    if (pathname.includes('/dashboard/api')) {
        return 'api'
    }
    if (pathname.includes('/intelligence') || pathname.includes('research')) {
        return 'discovery'
    }
    return 'reporting'
}

export const MainSidebar = ({ isExpanded, setIsExpanded, isTestUser }) => {

    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { user, logout } = useAuthContext()
    const { brands } = useWatchList()
    const userInitial = user.name?.slice(0, 1).toUpperCase()

    // Swap mode: secondary areas replace the body with their own subnav.
    let swapSubnav = null
    if (pathname.includes('settings')) {
        swapSubnav = <SettingsSubNavbar />
    } else if (pathname.includes('/chat')) {
        swapSubnav = <ChatSubNavbar />
    } else if (pathname.includes('clips-library')) {
        swapSubnav = <ClipsLibrarySubNavbar />
    }
    const swapModeActive = !!swapSubnav

    const [watchingOpen, setWatchingOpen] = usePersistedState('sidebar:section:watching', true)
    const [addBrandOpen, setAddBrandOpen] = useState(false)

    // Active area follows the route (deep links + in-body nav stay in sync).
    const activeArea = getAreaFromPathname(pathname)

    const handleSelectArea = (area) => {
        if (area === activeArea) {
            return
        }
        AnalyticsService.track('nav-area-switched', { area, from: activeArea })
        if (area === 'discovery') {
            navigate('intelligence/discovery', { state: { entry: 'nav' } })
        } else if (area === 'api') {
            navigate('api')
        } else {
            navigate('creative/home')
        }
    }

    // Keep the Watching section open when the user is on a watching or
    // watched-brand route (Boards + Library are their own top-level links now).
    const isWatchingArea = pathname.includes('/intelligence/watching') || pathname.includes('/intelligence/brands')

    const toggleWatching = () => setWatchingOpen(o => !o)

    // Never leave the Watching section collapsed when the user lands on it.
    // (Harmless in swap mode — the sections aren't rendered there anyway.)
    useEffect(() => {
        if (isWatchingArea) {
            setWatchingOpen(true)
        }
    }, [pathname, isWatchingArea])

    // Onboarding is a full-screen takeover — hide the sidebar entirely, then
    // open it once the user lands in the app for the first time afterwards.
    const isOnboarding = pathname.includes('onboarding')
    const wasOnboardingRef = useRef(false)
    useEffect(() => {
        if (isOnboarding) {
            setIsExpanded(false)
            wasOnboardingRef.current = true
            return
        }
        if (wasOnboardingRef.current) {
            wasOnboardingRef.current = false
            setIsExpanded(true)
        }
    }, [pathname, isOnboarding])

    return (
        <>
            {/* Floating restore button — visible only when the sidebar is hidden
                (suppressed on onboarding, which is a full-screen takeover) */}
            {!isExpanded && !isOnboarding && (
                <Button
                    variant="outline"
                    size="icon"
                    className="fixed top-4 left-4 z-[60] h-8 w-8 rounded-full"
                    onClick={() => setIsExpanded(true)}
                >
                    <Icon icon="lucide:chevrons-right" className="h-4 w-4" />
                </Button>
            )}

            <div
                className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-background border-r shadow-[4px_0_24px_-12px_rgba(0,0,0,0.18)] transition-transform duration-300 ${SIDEBAR_WIDTH} ${isExpanded ? 'translate-x-0' : '-translate-x-full'}`}
            >
                {/* Header — logo + Space switcher + collapse toggle. Hovering the
                    row highlights it and reveals the collapse button + the space
                    selector's chevron (both hidden at rest for a cleaner look). */}
                <div className="group/topbar mx-2 mt-2 flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-accent">
                    <button
                        type="button"
                        onClick={() => navigate('creative/home')}
                        className="shrink-0 transition-opacity hover:opacity-80"
                        title="Home"
                    >
                        <img src="/favicon-main.png" alt="Home" className="h-7 w-7 rounded-md" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <SpaceSelector triggerClassName="w-full h-8 justify-start gap-1.5 px-1 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-0 [&>svg]:transition-opacity group-hover/topbar:[&>svg]:opacity-100" />
                    </div>
                    {!isTestUser && !isOnboarding && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 opacity-0 pointer-events-none transition-opacity group-hover/topbar:opacity-100 group-hover/topbar:pointer-events-auto"
                            onClick={() => setIsExpanded(false)}
                            title="Hide sidebar"
                        >
                            <Icon icon="lucide:chevrons-left" className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {/* Area switcher — Reporting / Discovery / API (hidden during
                    Settings/Chat/Clips takeovers, which own the whole body) */}
                {!swapModeActive && (
                    <SidebarAreaSwitcher activeArea={activeArea} onSelect={handleSelectArea} />
                )}

                {/* Trial / Free-plan banner — one place under the module icons,
                    shared across all three areas. */}
                {!swapModeActive && <SidebarBanner className="mx-3 mt-2" />}

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {swapModeActive ? (
                        <div className="flex h-full flex-col">
                            <button
                                type="button"
                                onClick={() => navigate('creative/home')}
                                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
                            >
                                <Icon icon="lucide:chevron-left" className="h-4 w-4" />
                                Back
                            </button>
                            {/* Intentional nested scroll: inner h-full flex child fills the outer, so only one scroll bar is active at a time. */}
                            <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {swapSubnav}
                            </div>
                        </div>
                    ) : (
                        <motion.div
                            key={activeArea}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.18 }}
                            className="flex flex-col py-2"
                        >
                            {activeArea === 'reporting' && (
                                <>
                                    {/* Top routes — Home, Weekly Insights */}
                                    <div className="px-3 space-y-0.5">
                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('home') && !pathname.includes('account-overview') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('creative/home')}
                                        >
                                            <Icon icon="ph:house" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Home</span>
                                        </div>

                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('insights') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('creative/insights')}
                                        >
                                            <Icon icon="fluent:brain-sparkle-20-regular" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Weekly Insights</span>
                                        </div>
                                    </div>

                                    {/* Sections — Dashboards + Reports (top links suppressed) */}
                                    <CreativeAnalyticsSubNavbar showTopLinks={false} />
                                </>
                            )}

                            {activeArea === 'discovery' && (
                                <>
                                    {/* Top links: Highlights + Ads Library + Boards */}
                                    <div className="px-3 space-y-0.5">
                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('/intelligence/discovery') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('intelligence/discovery', { state: { entry: 'nav' } })}
                                        >
                                            <Icon icon="lucide:flame" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Highlights</span>
                                        </div>

                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('/intelligence/library') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('intelligence/library')}
                                            data-intercom-target="sidebar-ads-library"
                                        >
                                            <Icon icon="mdi:magnify" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Ads Library</span>
                                        </div>

                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('/intelligence/landing-pages') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('intelligence/landing-pages')}
                                            data-intercom-target="sidebar-landing-pages"
                                        >
                                            <Icon icon="mdi:web" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Landing Pages</span>
                                            <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[9px] font-medium uppercase leading-4 tracking-wide text-primary ring-1 ring-inset ring-primary/40">New</span>
                                        </div>

                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('/intelligence/advertisers') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('intelligence/advertisers')}
                                        >
                                            <Icon icon="lucide:megaphone" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Advertisers</span>
                                        </div>

                                        <div
                                            className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${pathname.includes('/intelligence/boards') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                            onClick={() => navigate('intelligence/boards')}
                                        >
                                            <Icon icon="mdi:bookmark-multiple-outline" className="h-4 w-4" />
                                            <span className="text-[13px] font-light">Boards</span>
                                        </div>
                                    </div>

                                    {/* Section — Watching (followed advertisers).
                                        mt-2 matches Reporting's top-links→section gap. */}
                                    <div className="mt-2">
                                    <CollapsibleNavSection
                                        label="Watch List"
                                        count={brands.length > 0 ? brands.length : undefined}
                                        isOpen={watchingOpen}
                                        onToggle={toggleWatching}
                                        action={
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 mr-1"
                                                onClick={() => setAddBrandOpen(true)}
                                                title="Search advertiser"
                                            >
                                                <Icon icon="ic:baseline-plus" className="h-6 w-6 rounded-full bg-primary/10 p-1 text-primary" />
                                            </Button>
                                        }
                                    >
                                        <WatchedBrandsList />
                                    </CollapsibleNavSection>
                                    </div>
                                </>
                            )}

                            {activeArea === 'api' && (
                                <div className="px-3 space-y-0.5">
                                    {API_NAV.map((item) => {
                                        const isActive = item.match(pathname)
                                        return (
                                            <div
                                                key={item.to}
                                                className={`flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                                onClick={() => { trackUiEvent('sidebar-navigation-clicked', { element: 'sidebar', destination: item.to }); navigate(item.to) }}
                                            >
                                                <Icon icon={item.icon} className="h-4 w-4" />
                                                <span className="text-[13px] font-light">{item.label}</span>
                                            </div>
                                        )
                                    })}
                                    <NavLink
                                        to="intelligence/advertisers"
                                        onClick={() => trackUiEvent('api-advertiser-discovery-clicked', { element: 'sidebar' })}
                                        className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors hover:bg-accent"
                                    >
                                        <Icon icon="lucide:search" className="h-4 w-4 shrink-0" />
                                        <span className="text-[13px] font-light">Advertiser discovery</span>
                                        <Icon icon="lucide:external-link" className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
                                    </NavLink>
                                    <a
                                        href="https://public-api.upspring.ai/v1/docs/ui"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => AnalyticsService.track('api-docs-clicked', { source: 'sidebar' })}
                                        className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors hover:bg-accent"
                                    >
                                        <Icon icon="lucide:book-open" className="h-4 w-4" />
                                        <span className="text-[13px] font-light">Docs</span>
                                        <Icon icon="lucide:external-link" className="ml-auto h-3 w-3 text-muted-foreground" />
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>

                {/* Footer — utilities moved down from the old rail */}
                <div className="flex items-center gap-1 px-3 py-2 border-t">
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <a
                                    href="https://help.upspring.ai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`p-2 text-muted-foreground hover:text-foreground transition-colors ${isTestUser ? 'hidden' : ''}`}
                                >
                                    <Icon icon="mdi:help-circle-outline" className="h-4 w-4" />
                                </a>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-slate-800 text-white text-xs">Help Center</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <NavLink
                                    to="settings"
                                    className={({ isActive }) => `p-2 transition-colors ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'} ${isTestUser ? 'hidden' : ''}`}
                                >
                                    <Icon icon="uil:setting" className="h-4 w-4" />
                                </NavLink>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-slate-800 text-white text-xs">Settings</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <RoleGuard minimumRoleRequired={UserRoles.ADMIN} hideContent>
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => toggleInvitationDialog(true)}
                                        className={`p-2 text-muted-foreground hover:text-foreground transition-colors ${isTestUser ? 'hidden' : ''}`}
                                    >
                                        <Icon icon="mdi:invite" className="h-4 w-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-slate-800 text-white text-xs">Invite Member</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </RoleGuard>

                    <div className="ml-auto">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Avatar className="border-primary-200 border h-8 w-8 text-sm cursor-pointer">
                                    <AvatarImage src={user.profileImage} alt={user.name} />
                                    <AvatarFallback>{userInitial}</AvatarFallback>
                                </Avatar>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-4">
                                <div className="flex flex-col items-center space-y-4">
                                    <Avatar className="w-14 h-14 border">
                                        <AvatarImage src={user.profileImage} alt={user.name} />
                                        <AvatarFallback className="text-2xl">{userInitial}</AvatarFallback>
                                    </Avatar>
                                    <div className="text-center">
                                        <TypographyLarge>Hello, {user.name}</TypographyLarge>
                                        <TypographySmallMuted className="font-light">Welcome back!</TypographySmallMuted>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="w-full flex gap-2"
                                        onClick={() => {
                                            AnalyticsService.track('user-logged-out', { userName: user.name, userId: user._id })
                                            logout()
                                        }}
                                    >
                                        <Icon icon="solar:logout-2-broken" />
                                        Logout
                                    </Button>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </div>

            <AddBrandDialog open={addBrandOpen} onClose={() => setAddBrandOpen(false)} />
        </>
    )
}
